import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, renderSlot as _renderSlot, createVNode as _createVNode, withCtx as _withCtx, vShow as _vShow, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = { class: "relative" }
const _hoisted_2 = {
  key: 1,
  class: "absolute inset-x-0 inset-y-0 bg-curtain-color z-[100]"
}
const _hoisted_3 = { class: "payment-method-container flex flex-col divide-y" }
const _hoisted_4 = { key: 0 }
const _hoisted_5 = { key: 1 }
const _hoisted_6 = { class: "border-t" }
const _hoisted_7 = { class: "flex w-full pt-4" }
const _hoisted_8 = { class: "flex w-full pt-4" }

import { RichText } from '@sitecore-jss/sitecore-jss-vue';
import { computed, ref, onMounted, watch } from 'vue';
import PaymentSectionFZIframe from './PaymentSectionFZIframe.vue';
import { usePaymentStore } from '@/store';
import { IPaymentSectionProps } from '@/interfaces';
import { PaymentTypes } from '@/enums/payment-types.enum';
import { FormKit } from '@formkit/vue';
import PaymentSectionGooglePay from './PaymentSectionGooglePay.vue';
import PaymentSectionApplePay from './PaymentSectionApplePay.vue';
import {
  GOOGLE_PAY_ENVIRONMENT,
  GOOGLE_PAY_GATEWAY_MERCHANT_ID,
  GOOGLE_PAY_MERCHANT_ID,
  GOOGLE_PAY_MERCHANT_NAME,
} from '@/constants/urls.const';
import { isMacOS } from '@/utils/dom-helpers';


export default /*@__PURE__*/_defineComponent({
  __name: 'PaymentSection',
  props: {
    AcceptPaymentTypes: {},
    BankAccountTitle: {},
    AccountNameInvalidMessage: {},
    AccountNameLabel: {},
    AccountNameRequiredMessage: {},
    AccountNameTooltip: {},
    AccountNumberInvalidMessage: {},
    AccountNumberLabel: {},
    AccountNumberRequiredMessage: {},
    BsbInvalidMessage: {},
    BsbLabel: {},
    BsbNotFoundMessage: {},
    BsbRequiredMessage: {},
    CardTypeTitle: {},
    CardIcons: {},
    PaymentDisclaimer: {},
    BankPaymentDisclaimer: {},
    ShowCardIcons: {},
    SubmitPaymentButtonLabel: {},
    BankAccountIcon: {},
    PayLaterTitle: {},
    PayLaterIcon: {},
    SubmitPayLaterLabel: {},
    SubmitBankAccountLabel: {},
    PayLaterContent: {},
    PaymentMethodTitle: {},
    GooglePayIcon: {},
    ApplePayIcon: {},
    ApplePayEnableDesktop: {},
    ApplePayEnableMacOS: {}
  },
  setup(__props: any) {

const props = __props;
const paymentStore = usePaymentStore();

const bankAccountName = ref<any>(null);
const bsbInput = ref<any>(null);
const bankAccountNumber = ref<any>(null);
const checkingBsb = ref<boolean>(false);

const acceptCardPayment = computed(() => {
  return props.AcceptPaymentTypes.some((type) => type.name == PaymentTypes[PaymentTypes.CreditCard]);
});

const acceptBankAccount = computed(() => {
  return props.AcceptPaymentTypes.some((type) => type.name == PaymentTypes[PaymentTypes.BankAccount]);
});

const acceptPayLater = computed(() => {
  return props.AcceptPaymentTypes.some((type) => type.name == PaymentTypes[PaymentTypes.PayLater]);
});

const acceptGooglePay = computed(() => {
  return props.AcceptPaymentTypes.some((type) => type.name == PaymentTypes[PaymentTypes.GooglePay]);
});

const acceptApplePay = computed(() => {
  try {
    if (isMacOS() && !props.ApplePayEnableMacOS?.value) {
      return false;
    }
    const supportsApplePay =
      typeof window !== 'undefined' &&
      window.isSecureContext && // ensure HTTPS or localhost with valid cert
      (window as any).ApplePaySession &&
      typeof (window as any).ApplePaySession.canMakePayments === 'function' &&
      (window as any).ApplePaySession.canMakePayments();

    return (
      props.AcceptPaymentTypes.some((type) => type.name === PaymentTypes[PaymentTypes.ApplePay]) &&
      (supportsApplePay || props.ApplePayEnableDesktop?.value)
    );
  } catch (e) {
    console.warn('Apple Pay detection failed:', e);
    return false;
  }
});

const isMasked = computed(() => paymentStore.disablePaymentPanel);

const paymentTypeSelectable = computed(() => {
  return props.AcceptPaymentTypes.length > 0;
});

const selectedPaymentType = computed({
  get: () => paymentStore.selectedPaymentMethod,
  set: (value) => {
    paymentStore.selectedPaymentMethod = value;
  },
});

const financialInstitutionName = computed(() => {
  return paymentStore.bsbBranchDetails?.name
    ? `${paymentStore.bsbBranchDetails?.name}, ${paymentStore.bsbBranchDetails?.branch}`
    : undefined;
});

onMounted(async () => {
  setDefaultPaymentMethod();
});

// Re-evaluate the default when accepted payment types change dynamically (e.g. direct debit yes/no)
watch(
  () => props.AcceptPaymentTypes,
  () => {
    setDefaultPaymentMethod();
  }
);

function setDefaultPaymentMethod() {
  // Only set the default payment method if the user hasn't already made a selection,
  // or if their current selection is no longer available in the accepted types.
  const currentSelection = paymentStore.selectedPaymentMethod;
  const isCurrentSelectionAvailable =
    currentSelection != null && props.AcceptPaymentTypes.some((type) => type.name === PaymentTypes[currentSelection]);

  if (!isCurrentSelectionAvailable && props.AcceptPaymentTypes?.length > 0) {
    paymentStore.selectedPaymentMethod = paymentStore.getDefaultSelectedPaymentMethod(props.AcceptPaymentTypes);
  }
}

const bsbLookup = async function ({ value }: { value: string }) {
  if (value?.length != 7) return;
  checkingBsb.value = true;
  try {
    const bsb = value.replace('-', '');
    const res = await paymentStore.bsbLookup(bsb);
    return res;
  } catch (error) {
    return false;
  } finally {
    checkingBsb.value = false;
  }
};

const replaceBsb = (message: string, bsb: string) => {
  return message?.replace('{{bsb}}', bsb);
};

const submitPayment = async (paymentType: PaymentTypes) => {
  try {
    const bsb = bsbInput.value?.node?.value;
    const accountName = bankAccountName?.value?.node?.value;
    const accountNumber = bankAccountNumber?.value?.node?.value;

    if (bsb && accountName && accountNumber) {
      await paymentStore.setDirectDebitDetails(accountName, bsb, accountNumber);
    }

    paymentStore.submitPaymentCallback && paymentStore.submitPaymentCallback(paymentType);
  } catch (e) {
    console.error(e);
  }
};

/**
 * Change payment method handler
 *
 * @param paymentMethod
 */
const changePaymentMethod = (paymentMethod: PaymentTypes) => {
  if (!paymentTypeSelectable.value) return;
  paymentStore.changePaymentMethod(paymentMethod);

  //Reset iframe otherwise it can be blank sometimes.
  if (paymentMethod === PaymentTypes.CreditCard) {
    paymentStore.resetIframe();
  }
};

// Lint BSB Input
(window as any).RACQLintBSBInput = (target: { value: string }) => {
  try {
    if (!target?.value || typeof target.value != 'string') {
      paymentStore.bsbBranchDetails = null;
      return;
    }
    const bsb = target?.value;
    const bsbAr = bsb.replace(/[^0-9/]/g, '').split('');
    bsbAr.length > 3 && bsbAr.splice(3, 0, '-');
    target.value = bsbAr.join('').slice(0, 7);
    if (!target.value) {
      paymentStore.bsbBranchDetails = null;
    }
  } catch (error) {
    console.warn('Unable to parse bsb: ', error);
  }
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (_ctx.PaymentMethodTitle?.value)
      ? (_openBlock(), _createBlock(_unref(RichText), {
          key: 0,
          field: _ctx.PaymentMethodTitle,
          class: "text-racq-dark-grey text-xl-bold mb-6"
        }, null, 8, ["field"]))
      : _createCommentVNode("", true),
    (isMasked.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_3, [
      (acceptCardPayment.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
            _createElementVNode("div", {
              class: _normalizeClass(["card-type-section flex space-between items-center py-4", paymentTypeSelectable.value && 'hover:bg-racq-white-background cursor-pointer']),
              onClick: _cache[1] || (_cache[1] = () => changePaymentMethod(_unref(PaymentTypes).CreditCard))
            }, [
              (paymentTypeSelectable.value)
                ? (_openBlock(), _createBlock(_unref(FormKit), {
                    key: 0,
                    modelValue: selectedPaymentType.value,
                    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((selectedPaymentType).value = $event)),
                    type: "nativeRadio",
                    name: "paymentTypeSelection",
                    outerClass: "-mt-1",
                    options: [{ label: 'CreditCard', value: _unref(PaymentTypes).CreditCard, srOnly: true }]
                  }, null, 8, ["modelValue", "options"]))
                : _createCommentVNode("", true),
              (_ctx.CardTypeTitle?.value)
                ? (_openBlock(), _createBlock(_unref(RichText), {
                    key: 1,
                    field: _ctx.CardTypeTitle,
                    class: "card-type-title text-base-bold text-racq-dark-grey"
                  }, null, 8, ["field"]))
                : _createCommentVNode("", true),
              (_ctx.ShowCardIcons?.value && _ctx.CardIcons?.value)
                ? (_openBlock(), _createBlock(_unref(RichText), {
                    key: 2,
                    class: "card-type-icons flex ml-auto gap-1 h-6",
                    field: _ctx.CardIcons
                  }, null, 8, ["field"]))
                : _createCommentVNode("", true)
            ], 2)
          ]))
        : _createCommentVNode("", true),
      (acceptBankAccount.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
            _createElementVNode("div", {
              class: _normalizeClass(["card-type-section flex space-between items-center py-4", paymentTypeSelectable.value && 'hover:bg-racq-white-background cursor-pointer']),
              onClick: _cache[3] || (_cache[3] = () => changePaymentMethod(_unref(PaymentTypes).BankAccount))
            }, [
              (paymentTypeSelectable.value)
                ? (_openBlock(), _createBlock(_unref(FormKit), {
                    key: 0,
                    modelValue: selectedPaymentType.value,
                    "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((selectedPaymentType).value = $event)),
                    type: "nativeRadio",
                    name: "paymentTypeSelection",
                    outerClass: "-mt-1",
                    options: [{ label: 'BankAccount', value: _unref(PaymentTypes).BankAccount, srOnly: true }]
                  }, null, 8, ["modelValue", "options"]))
                : _createCommentVNode("", true),
              (_ctx.BankAccountTitle?.value)
                ? (_openBlock(), _createBlock(_unref(RichText), {
                    key: 1,
                    field: _ctx.BankAccountTitle,
                    class: "card-type-title text-base-bold text-racq-dark-grey"
                  }, null, 8, ["field"]))
                : _createCommentVNode("", true),
              (_ctx.BankAccountIcon?.value)
                ? (_openBlock(), _createBlock(_unref(RichText), {
                    key: 2,
                    class: "card-type-icons flex ml-auto gap-2 h-6",
                    field: _ctx.BankAccountIcon
                  }, null, 8, ["field"]))
                : _createCommentVNode("", true)
            ], 2)
          ]))
        : _createCommentVNode("", true),
      (acceptGooglePay.value)
        ? (_openBlock(), _createElementBlock("div", {
            key: 2,
            class: _normalizeClass(["card-type-section flex space-between items-center py-4", paymentTypeSelectable.value && 'hover:bg-racq-white-background cursor-pointer']),
            onClick: _cache[5] || (_cache[5] = () => changePaymentMethod(_unref(PaymentTypes).GooglePay))
          }, [
            (paymentTypeSelectable.value)
              ? (_openBlock(), _createBlock(_unref(FormKit), {
                  key: 0,
                  modelValue: selectedPaymentType.value,
                  "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((selectedPaymentType).value = $event)),
                  type: "nativeRadio",
                  name: "paymentTypeSelection",
                  outerClass: "-mt-1",
                  options: [{ label: 'GooglePay', value: _unref(PaymentTypes).GooglePay, srOnly: true }]
                }, null, 8, ["modelValue", "options"]))
              : _createCommentVNode("", true),
            _cache[9] || (_cache[9] = _createElementVNode("span", { class: "card-type-title text-base-bold text-racq-dark-grey" }, "Google Pay", -1)),
            (_ctx.GooglePayIcon?.value)
              ? (_openBlock(), _createBlock(_unref(RichText), {
                  key: 1,
                  class: "card-type-icons flex ml-auto gap-1 h-6",
                  field: _ctx.GooglePayIcon
                }, null, 8, ["field"]))
              : _createCommentVNode("", true)
          ], 2))
        : _createCommentVNode("", true),
      (acceptApplePay.value)
        ? (_openBlock(), _createElementBlock("div", {
            key: 3,
            class: _normalizeClass(["card-type-section flex space-between items-center py-4", paymentTypeSelectable.value && 'hover:bg-racq-white-background cursor-pointer']),
            onClick: _cache[7] || (_cache[7] = () => changePaymentMethod(_unref(PaymentTypes).ApplePay))
          }, [
            (paymentTypeSelectable.value)
              ? (_openBlock(), _createBlock(_unref(FormKit), {
                  key: 0,
                  modelValue: selectedPaymentType.value,
                  "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((selectedPaymentType).value = $event)),
                  type: "nativeRadio",
                  name: "paymentTypeSelection",
                  outerClass: "-mt-1",
                  options: [{ label: 'ApplePay', value: _unref(PaymentTypes).ApplePay, srOnly: true }]
                }, null, 8, ["modelValue", "options"]))
              : _createCommentVNode("", true),
            _cache[10] || (_cache[10] = _createElementVNode("span", { class: "card-type-title text-base-bold text-racq-dark-grey" }, "Apple Pay", -1)),
            (_ctx.ApplePayIcon?.value)
              ? (_openBlock(), _createBlock(_unref(RichText), {
                  key: 1,
                  class: "card-type-icons flex ml-auto gap-1 h-6",
                  field: _ctx.ApplePayIcon
                }, null, 8, ["field"]))
              : _createCommentVNode("", true)
          ], 2))
        : _createCommentVNode("", true),
      _withDirectives(_createElementVNode("div", _hoisted_6, [
        (acceptCardPayment.value)
          ? (_openBlock(), _createElementBlock("div", {
              key: 0,
              class: _normalizeClass(["iframe-section ease-in-out duration-500 transition-all my-8 max-h-[1000px]", selectedPaymentType.value != _unref(PaymentTypes).CreditCard && '!overflow-hidden !max-h-[0px] !my-0 opacity-0'])
            }, [
              _renderSlot(_ctx.$slots, "default", {}, () => [
                _createVNode(PaymentSectionFZIframe)
              ]),
              (_ctx.PaymentDisclaimer?.value)
                ? (_openBlock(), _createBlock(_unref(RichText), {
                    key: 0,
                    field: _ctx.PaymentDisclaimer,
                    class: "disclaimer-section flex flex-col text-sm font-normal"
                  }, null, 8, ["field"]))
                : _createCommentVNode("", true)
            ], 2))
          : _createCommentVNode("", true),
        (
            acceptBankAccount.value &&
            selectedPaymentType.value == _unref(PaymentTypes).BankAccount &&
            (!_unref(paymentStore).applyTermsAndConditionsValidationFix || _unref(paymentStore).termsAndConditionsAccepted)
          )
          ? (_openBlock(), _createElementBlock("div", {
              key: 1,
              class: _normalizeClass(["ease-in-out duration-500 transition-all my-8 px-3 max-h-[1000px]", selectedPaymentType.value != _unref(PaymentTypes).BankAccount && '!overflow-hidden !max-h-[0px] !my-0 opacity-0'])
            }, [
              _createVNode(_unref(FormKit), {
                type: "group",
                name: "payment-section-bank-account"
              }, {
                default: _withCtx(({ state: { valid } }) => [
                  _createVNode(_unref(FormKit), {
                    id: "bank-account-account-name",
                    ref_key: "bankAccountName",
                    ref: bankAccountName,
                    type: "text",
                    label: props.AccountNameLabel?.value,
                    outerClass: "mb-6",
                    innerClass: "input-md",
                    name: "bank-account-account-name",
                    tooltip: {
                  content: props.AccountNameTooltip?.value,
                  tooltipId: 'bank-account-account-name__tooltip_id',
                  tooltipOuterId: 'bank-account-account-name__outer',
                },
                    validation: [
                  ['required', 'trim'],
                  ['matches', /^[a-zA-Z0-9 -]{1,100}$/],
                ],
                    validationMessages: {
                  required: props.AccountNameRequiredMessage?.value,
                  matches: props.AccountNameInvalidMessage?.value,
                }
                  }, null, 8, ["label", "tooltip", "validationMessages"]),
                  _createVNode(_unref(FormKit), {
                    id: "bank-account-bsb",
                    ref_key: "bsbInput",
                    ref: bsbInput,
                    type: "text",
                    oninput: "window.RACQLintBSBInput(this)",
                    label: props.BsbLabel?.value,
                    outerClass: "mb-4",
                    innerClass: "input-xs",
                    name: "bank-account-bsb",
                    help: financialInstitutionName.value,
                    loading: checkingBsb.value,
                    validation: [['required', 'trim'], ['matches', /^\d{3}-\d{3}$/], ['bsbLookup']],
                    validationRules: { bsbLookup: bsbLookup },
                    validationMessages: {
                  required: props.BsbRequiredMessage?.value,
                  matches: props.BsbInvalidMessage?.value,
                  bsbLookup: props.BsbNotFoundMessage?.value,
                }
                  }, {
                    messages: _withCtx((context) => [
                      (context.messages?.rule_bsbLookup?.value)
                        ? (_openBlock(), _createBlock(_unref(RichText), {
                            key: 0,
                            class: _normalizeClass(context.classes?.message),
                            field: { value: replaceBsb(context.messages?.rule_bsbLookup?.value, context._value) }
                          }, null, 8, ["class", "field"]))
                        : _createCommentVNode("", true),
                      (context.messages?.rule_required?.value)
                        ? (_openBlock(), _createBlock(_unref(RichText), {
                            key: 1,
                            class: _normalizeClass(context.classes?.message),
                            field: context.messages?.rule_required
                          }, null, 8, ["class", "field"]))
                        : _createCommentVNode("", true),
                      (context.messages?.rule_matches?.value)
                        ? (_openBlock(), _createBlock(_unref(RichText), {
                            key: 2,
                            class: _normalizeClass(context.classes?.message),
                            field: context.messages?.rule_matches
                          }, null, 8, ["class", "field"]))
                        : _createCommentVNode("", true)
                    ]),
                    _: 1
                  }, 8, ["label", "help", "loading", "validationRules", "validationMessages"]),
                  _createVNode(_unref(FormKit), {
                    id: "bank-account-account-number",
                    ref_key: "bankAccountNumber",
                    ref: bankAccountNumber,
                    type: "tel",
                    label: props.AccountNumberLabel?.value,
                    outerClass: "mb-4",
                    innerClass: "input-sm",
                    name: "bank-account-account-number",
                    validation: [
                  ['required', 'trim'],
                  ['matches', /^(\d{6,9})$/],
                ],
                    validationMessages: {
                  required: props.AccountNumberRequiredMessage?.value,
                  matches: props.AccountNumberInvalidMessage?.value,
                }
                  }, null, 8, ["label", "validationMessages"]),
                  _createElementVNode("div", _hoisted_7, [
                    _createVNode(_unref(FormKit), {
                      type: "submit",
                      prefixIcon: "lockIcon",
                      prefixIconClass: "$remove:-ml-2 $remove:mr-2",
                      label: _ctx.SubmitBankAccountLabel?.value,
                      inputClass: "btn-fatzebra btn-edit",
                      outerClass: "ms-auto",
                      onClick: () => valid && submitPayment(_unref(PaymentTypes).BankAccount)
                    }, null, 8, ["label", "onClick"])
                  ]),
                  (_ctx.BankPaymentDisclaimer?.value)
                    ? (_openBlock(), _createBlock(_unref(RichText), {
                        key: 0,
                        field: _ctx.BankPaymentDisclaimer,
                        class: "disclaimer-section flex flex-col text-sm font-normal mt-8"
                      }, null, 8, ["field"]))
                    : _createCommentVNode("", true)
                ]),
                _: 1
              })
            ], 2))
          : _createCommentVNode("", true),
        (acceptPayLater.value)
          ? (_openBlock(), _createElementBlock("div", {
              key: 2,
              class: _normalizeClass(["ease-in-out duration-500 transition-all my-8 px-3 max-h-[1000px]", selectedPaymentType.value != _unref(PaymentTypes).PayLater && '!overflow-hidden !max-h-[0px] !my-0 opacity-0'])
            }, [
              (_ctx.PayLaterContent?.value)
                ? (_openBlock(), _createBlock(_unref(RichText), {
                    key: 0,
                    field: _ctx.PayLaterContent
                  }, null, 8, ["field"]))
                : _createCommentVNode("", true),
              _createElementVNode("div", _hoisted_8, [
                _createVNode(_unref(FormKit), {
                  type: "button",
                  label: _ctx.SubmitPayLaterLabel?.value,
                  inputClass: "btn btn-secondary input-cta-arrow-right",
                  onClick: _cache[8] || (_cache[8] = () => submitPayment(_unref(PaymentTypes).PayLater))
                }, null, 8, ["label"])
              ])
            ], 2))
          : _createCommentVNode("", true),
        (acceptGooglePay.value)
          ? (_openBlock(), _createElementBlock("div", {
              key: 3,
              class: _normalizeClass(selectedPaymentType.value != _unref(PaymentTypes).GooglePay && '!overflow-hidden !max-h-[0px] !my-0 opacity-0')
            }, [
              _createVNode(PaymentSectionGooglePay, {
                environment: _unref(GOOGLE_PAY_ENVIRONMENT),
                gatewayMerchantId: _unref(GOOGLE_PAY_GATEWAY_MERCHANT_ID),
                merchantName: _unref(GOOGLE_PAY_MERCHANT_NAME),
                merchantId: _unref(GOOGLE_PAY_MERCHANT_ID)
              }, null, 8, ["environment", "gatewayMerchantId", "merchantName", "merchantId"]),
              (_ctx.PaymentDisclaimer?.value)
                ? (_openBlock(), _createBlock(_unref(RichText), {
                    key: 0,
                    field: _ctx.PaymentDisclaimer,
                    class: "disclaimer-section flex flex-col text-sm font-normal"
                  }, null, 8, ["field"]))
                : _createCommentVNode("", true)
            ], 2))
          : _createCommentVNode("", true),
        (acceptApplePay.value)
          ? (_openBlock(), _createElementBlock("div", {
              key: 4,
              class: _normalizeClass(selectedPaymentType.value != _unref(PaymentTypes).ApplePay && '!overflow-hidden !max-h-[0px] !my-0 opacity-0')
            }, [
              _createVNode(PaymentSectionApplePay),
              (_ctx.PaymentDisclaimer?.value)
                ? (_openBlock(), _createBlock(_unref(RichText), {
                    key: 0,
                    field: _ctx.PaymentDisclaimer,
                    class: "disclaimer-section flex flex-col text-sm font-normal"
                  }, null, 8, ["field"]))
                : _createCommentVNode("", true)
            ], 2))
          : _createCommentVNode("", true)
      ], 512), [
        [_vShow, selectedPaymentType.value]
      ])
    ])
  ]))
}
}

})